import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  Briefcase, MapPin, Clock, DollarSign, CheckCircle, ArrowRight,
  ArrowLeft, Building2, Users
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const { jobSlug } = useParams();

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (jobSlug && jobs.length > 0) {
      const job = jobs.find(j => j.slug === jobSlug);
      setSelectedJob(job || null);
    }
  }, [jobSlug, jobs]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/jobs/public`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Job Detail View
  if (selectedJob) {
    return (
      <div className="min-h-screen">
        <Header />
        
        {/* Hero */}
        <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/careers" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Positions
            </Link>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{selectedJob.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-slate-300">
              <span className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />{selectedJob.department}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />{selectedJob.location}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />{selectedJob.employment_type}
              </span>
              {selectedJob.salary_range && (
                <span className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />{selectedJob.salary_range}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">About This Role</h2>
                  <p className="text-slate-600 leading-relaxed">{selectedJob.description}</p>
                </div>

                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Responsibilities</h2>
                  <ul className="space-y-3">
                    {selectedJob.responsibilities.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Requirements</h2>
                  <ul className="space-y-3">
                    {selectedJob.requirements.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-4">Benefits</h2>
                    <ul className="space-y-3">
                      {selectedJob.benefits.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-slate-50 rounded-2xl p-6 sticky top-32">
                  <h3 className="font-bold text-slate-900 mb-4">Interested?</h3>
                  <p className="text-slate-600 text-sm mb-6">
                    Join our team and help us build exceptional homes for families in the Charlotte area.
                  </p>
                  <Link to={`/employment-application?job=${selectedJob.id}&position=${encodeURIComponent(selectedJob.title)}`}>
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700">
                      Apply for this Position
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Jobs List View
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Join Our Team
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Build your career with C3 Contracting. We're looking for talented individuals who share our passion for quality craftsmanship and exceptional customer service.
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Work With Us?</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              At C3 Contracting, we believe in building more than just homes — we build careers.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Growth Opportunities', desc: 'We invest in our team with training and advancement paths.' },
              { title: 'Competitive Pay', desc: 'Fair compensation that reflects your skills and dedication.' },
              { title: 'Great Culture', desc: 'A supportive team environment built on respect and integrity.' }
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Open Positions</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Open Positions</h3>
              <p className="text-slate-600 mb-6">
                We don't have any open positions right now, but we're always looking for talented people.
              </p>
              <Link to="/employment-application">
                <Button variant="outline">Submit General Application</Button>
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {jobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/careers/${job.slug}`}
                  onClick={() => setSelectedJob(job)}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-cyan-600 transition-colors mb-2">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />{job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />{job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />{job.employment_type}
                        </span>
                        {job.salary_range && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />{job.salary_range}
                          </span>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* General Application */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Don't see a position that fits? We'd still love to hear from you.</p>
            <Link to="/employment-application">
              <Button variant="outline" size="lg">
                Submit General Application
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
